import {
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  FoodDetailsBody,
} from "./styled"
import { Fragment, useMemo, useState } from "react"
import Button from "components/Buttons/Button"
import PopConfirm from "components/PopConfirm"
import { useSetPageTitle } from "libs/ga"
import { BackButton } from "components/Common/BackButton"
import FullPageLoading from "components/Layouts/FullPageLoading"
import NutrientInformation from "components/foodDetails/share/NutrientInformation"
import CtaBox from "components/Common/CtaBox"
import FoodImage from "components/foodDetails/share/FoodImage"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FOOD_DETAILS_BODY_ID } from "config"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import {
  useDeleteSchedule,
  useScheduleDetails,
  useUpdateSchedule,
} from "features/schedule/hooks/CRUDSchedule"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import TrashIcon from "components/Icons/TrashIcon"
import { validateScheduleConfig } from "utils/validator"
import { useNavigate, useSearchParams } from "react-router-dom"
import PATHS from "router/paths"
import { useBackToHome } from "features/meal/hooks"
import useMealDetailsForm from "features/meal/hooks/logMeal/useMealDetailsForm"
import {
  ScheduleBackPage,
  IScheduleConfig,
} from "model/food/types/schedule.types"
import {
  isAIScanMeal,
  isAIScanNutritionFactsMeal,
  isAIScanPackageMeal,
  isEdamaMeal,
  isMyMeal,
  isOpenFoodFactMeal,
} from "model/food/utils/typeGuards/mealTypeGuards"
import MealIngredients from "./MealIngredients"
import MyFoodNameAndPortion from "components/foodDetails/share/MyFoodNameAndPortion"
import EdamaNameAndPortion from "components/foodDetails/share/EdamaNameAndPortion"
import OpenFoodFactNameAndPortion from "components/foodDetails/share/OpenFoodFactNameAndPortion"
import OpenFoodFactDescription from "components/foodDetails/share/OpenFoodFactDescription"
import AIScanNameAndPortion from "components/foodDetails/share/AIScanNameAndPortion"
import FoodDetailsMealTypeSelection from "components/mealTypeSelection/FoodDetailsMealTypeSelection"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"

const ScheduleDetails = () => {
  useSetPageTitle("Schedule Details")
  const { scheduleDetails, getScheduleDetailsLoading } = useScheduleDetails()
  const [displaySection, setDisplaySection] = useState<
    "MEAL_FORM" | "EDIT_NUTRITION_FORM"
  >("MEAL_FORM")

  const {
    onPortionChange,
    onUnitChange,
    onMealTypeChange,
    meal,
    updateFoodAsManualNutrition,
  } = useMealDetailsForm({
    initMeal: scheduleDetails?.meal,
    mealType: scheduleDetails?.meal.type,
  })

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const backToHome = useBackToHome()

  const onBack = () => {
    const backPage = searchParams.get("from") as ScheduleBackPage
    if (backPage === ScheduleBackPage.MY_SCHEDULE) {
      navigate(PATHS.app.myFoods.schedules)
    } else {
      backToHome()
    }
  }

  const { handleDeleteSchedule, deleteScheduleLoading } =
    useDeleteSchedule(onBack)

  const [isPortionValid, setIsPortionValid] = useState(true)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)
  const [isPopConfirmDeleteOpen, setIsPopConfirmDeleteOpen] = useState(false)
  const [isPopConfirmUpdateOpen, setIsPopConfirmUpdateOpen] = useState(false)

  const handleBack = () => {
    const initMeal = scheduleDetails?.meal
    const isMealChanged =
      initMeal?.portion !== meal?.portion || initMeal?.unit !== meal?.unit
    const isScheduleInfoChanged =
      scheduleDetails?.type !== scheduleConfig?.type ||
      scheduleDetails?.startDate !== scheduleConfig?.startDate ||
      scheduleDetails?.endDate !== scheduleConfig?.endDate

    const isScheduleDayOfWeekChanged =
      scheduleDetails?.daysOfWeek?.length !==
        scheduleConfig?.daysOfWeek?.length ||
      scheduleDetails?.daysOfWeek?.some(
        (day) => !scheduleConfig?.daysOfWeek?.includes(day),
      )

    if (isMealChanged || isScheduleInfoChanged || isScheduleDayOfWeekChanged) {
      setIsPopConfirmDiscardOpen(true)
    } else {
      onBack()
    }
  }

  const initScheduleConfig = useMemo(() => {
    if (!scheduleDetails) return undefined

    const config: IScheduleConfig = {
      type: scheduleDetails.type,
      daysOfWeek: scheduleDetails.daysOfWeek ?? [],
      startDate: scheduleDetails.startDate,
      endDate: scheduleDetails.endDate,
    }

    return config
  }, [scheduleDetails])

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,
    getUpdateSchedulePayload,
  } = useScheduleFormSection(initScheduleConfig)

  const { handleUpdateSchedule, updateScheduleLoading } =
    useUpdateSchedule(onBack)

  const onSubmit = () => {
    if (scheduleDetails && meal) {
      const schedulePayload = getUpdateSchedulePayload(meal)
      if (schedulePayload) {
        handleUpdateSchedule({
          payload: schedulePayload,
          scheduleId: scheduleDetails.id,
        })
      }
    }
  }
  const isScheduleValid = validateScheduleConfig(scheduleConfig, !isNoEndDate)

  const hasIngredients = meal && (isMyMeal(meal) || isAIScanMeal(meal))

  if (displaySection === "EDIT_NUTRITION_FORM" && meal) {
    return (
      <EditNutrientForm
        onSubmit={updateFoodAsManualNutrition}
        meal={meal}
        onClose={() => {
          setDisplaySection("MEAL_FORM")
        }}
      />
    )
  }

  return (
    <Fragment>
      {getScheduleDetailsLoading || !scheduleDetails || !meal ? (
        <FullPageLoading />
      ) : (
        <Fragment>
          <FoodDetailsContent>
            <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
              <FoodImage previewImageUrl={meal.previewImageUrl}>
                <TopNavWrapper>
                  <FoodDetailsHeader>
                    <BackButton onClick={handleBack}>
                      <ChevronLeftBtn />
                    </BackButton>

                    <FoodDetailsMealTypeSelection
                      value={meal.type}
                      onChange={(val) => onMealTypeChange(val)}
                    />
                    <div style={{ width: 24, height: 24 }}></div>
                  </FoodDetailsHeader>
                </TopNavWrapper>
              </FoodImage>

              <FoodDetailsInfo>
                {isMyMeal(meal) && (
                  <MyFoodNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getScheduleDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={meal.gram}
                  />
                )}

                {(isAIScanMeal(meal) ||
                  isAIScanPackageMeal(meal) ||
                  isAIScanNutritionFactsMeal(meal)) && (
                  <AIScanNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getScheduleDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={meal.gram}
                  />
                )}

                {isEdamaMeal(meal) && (
                  <EdamaNameAndPortion
                    edama={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    onUnitChange={onUnitChange}
                    disabled={getScheduleDetailsLoading}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={meal.gram}
                    unitOptions={meal.units}
                  />
                )}

                {isOpenFoodFactMeal(meal) && (
                  <OpenFoodFactNameAndPortion
                    food={meal}
                    portionValue={meal.portion}
                    unitValue={meal.unit}
                    onPortionChange={handlePortionChange}
                    setIsPortionValid={setIsPortionValid}
                    calories={meal.calorie}
                    gram={meal.gram}
                  />
                )}

                <EditableNutrientInformation
                  entry={meal}
                  setEditMode={() => {
                    setDisplaySection("EDIT_NUTRITION_FORM")
                  }}
                />

                <ScheduleConfigFormSection
                  scheduleConfig={scheduleConfig}
                  onDayOfWeekChange={onDayOfWeekChange}
                  onSchedulePeriodChange={onSchedulePeriodChange}
                  onScheduleTypeChange={onScheduleTypeChange}
                  isNoEndDate={isNoEndDate}
                  onNoEndDateChange={onNoEndDateChange}
                  hasSchedule
                  setHasSchedule={() => {}}
                  isUpdateMode
                />

                {hasIngredients ? (
                  <MealIngredients ingredients={meal.ingredients}  isManualNutrition={meal.isManualNutrition}/>
                ) : undefined}

                {isOpenFoodFactMeal(meal) && <OpenFoodFactDescription />}
              </FoodDetailsInfo>
            </FoodDetailsBody>

            <AppStyleCtaBox>
              <Button
                onClick={() => setIsPopConfirmDeleteOpen(true)}
                $type="default"
                $isDeleteBtn
                style={{
                  padding: "var(--spacing_m, 12px) var(--spacing_l, 16px)",
                }}
              >
                <TrashIcon color="#FF4921" />
              </Button>

              <Button
                $type="primary"
                style={{ width: "100%", padding: "12px 16px" }}
                disabled={!isPortionValid || !isScheduleValid}
                onClick={() => {
                  setIsPopConfirmUpdateOpen(true)
                }}
              >
                Save
              </Button>
            </AppStyleCtaBox>
          </FoodDetailsContent>

          <PopConfirm
            isOpen={isPopConfirmDeleteOpen}
            title="Delete Schedule Meal?"
            onCancel={() => setIsPopConfirmDeleteOpen(false)}
            onConfirm={() => {
              if (scheduleDetails) handleDeleteSchedule(scheduleDetails.id)
            }}
            type="danger"
            loading={deleteScheduleLoading}
          >
            <div style={{ textAlign: "center" }}>
              This meal will be removed from all future logs. Do you want to
              proceed?
            </div>
          </PopConfirm>

          <PopConfirm
            isOpen={isPopConfirmDiscardOpen}
            title="Unsaved changes?"
            cancelText="Discard"
            confirmText="Save changes"
            onCancel={onBack}
            onConfirm={() => {
              setIsPopConfirmUpdateOpen(true)
              setIsPopConfirmDiscardOpen(false)
            }}
            loading={false}
          >
            <p style={{ textAlign: "center" }}>
              Do you want to save the changes?
            </p>
          </PopConfirm>

          <PopConfirm
            isOpen={isPopConfirmUpdateOpen}
            title="Update Scheduled Meal? "
            cancelText="No"
            confirmText="Yes"
            onCancel={() => setIsPopConfirmUpdateOpen(false)}
            onConfirm={() => {
              onSubmit()
            }}
            loading={updateScheduleLoading}
          >
            <p style={{ textAlign: "center" }}>
              This change will apply to all future logs. Do you want to proceed?
            </p>
          </PopConfirm>
        </Fragment>
      )}
    </Fragment>
  )
}

export default ScheduleDetails
